// Library Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
// Local Imports
import endPoints, { coreAPI } from '@/redux/constant';
import axios from '@/redux/http';
import { isAllowedUploadFile, ALLOWED_UPLOAD_ERROR_MESSAGE } from '@/utils';

// Initial State
const initialState = {
  loading: false,
  error: null,
  shareableData: null,
  socialMediaKitData: null,
  socialPostsCache: {},
  uploadImageLoading: false,
  uploadVideoLoading: false,
  deleteMediaKitLoading: false,
  removeAuthLoading: false,
  verifySocialLoading: false,
  getSocialPostLoading: false,
  getSocialPostsListLoading: false,
  getInstagramPostInsightLoading: false,
  getYoutubeVideoInsightLoading: false,
  getTiktokPostInsightLoading: false,
  saveReviewsLoading: false,
  sendMailLoading: false
};

// GET SHAREABLE LINK
export const getLiveMediaKitURLAPIFn = createAsyncThunk(
  'managekit/shareableLink',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${endPoints.liveMediaKitURL}?userId=${values?.userId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// GET SHAREABLE LINK
export const getLiveMediaKitSocialURLAPIFn = createAsyncThunk(
  'managekit/authenticateSocial',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${endPoints.mediaKitSocialURL}?userId=${values?.userId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// REMOVE SOCIAL CHANNEL AUTHENTICATION
export const removeAuthenticateSocialAPIFn = createAsyncThunk(
  'managekit/removeAuthenticateSocial',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${endPoints.removeSocialAuth}?userId=${values?.userId
        }&socialChannel=${values?.channel?.toLowerCase()}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// DELETE MEDIA KIT
export const deleteMediaKitAPIFn = createAsyncThunk(
  'managekit/deleteMediaKit',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${endPoints.deleteMediaKit}${values?.mediaKitId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// VERIFY SOCIAL MEDIA EXISTENCE
export const verifySocialMediaExistenceAPIFn = createAsyncThunk(
  'managekit/verifySocialMediaExistence',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(endPoints.verifySocialMediaExistenceURL(values));
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data || 'Failed to verify social media existence');
    }
  }
);

// VERIFY SOCIAL MEDIA EXISTENCE
export const getSocialPostFromUrlAPIFn = createAsyncThunk(
  'managekit/getSocialPostFromUrlAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(endPoints.getSocialMediaPostURL(values));
      return data;
    } catch (error) {
      // Try to return the full error response if it exists, especially for validation errors
      if (error?.response?.data) {
        // Handle validation error structure
        if (error.response.data?.responseException) {
          return rejectWithValue(error.response.data);
        }
        // Handle direct top-level errors
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error?.message);
    }
  }
);

// GET SOCIAL MEDIA POSTS LIST (for grid)
export const getSocialMediaPostsListAPIFn = createAsyncThunk(
  'managekit/getSocialMediaPostsListAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(endPoints.getSocialMediaPostsURL(values));
      if (data?.error || data?.responseException) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      if (error?.response?.data) return rejectWithValue(error.response.data);
      return rejectWithValue('Failed to get social media posts');
    }
  }
);

// GET INSTAGRAM POST INSIGHT (tile selection)
export const getInstagramPostInsightAPIFn = createAsyncThunk(
  'managekit/getInstagramPostInsightAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        endPoints.getInstagramPostInsightURL({ userId: values?.userId }),
        values?.payload
      );
      return data;
    } catch (error) {
      if (error?.response?.data) return rejectWithValue(error.response.data);
      return rejectWithValue('Failed to get Instagram post insight');
    }
  }
);

// GET YOUTUBE VIDEO INSIGHT (tile selection)
export const getYoutubeVideoInsightAPIFn = createAsyncThunk(
  'managekit/getYoutubeVideoInsightAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(endPoints.getYoutubeVideoInsightURL({ videoId: values?.videoId }));
      return data;
    } catch (error) {
      if (error?.response?.data) return rejectWithValue(error.response.data);
      return rejectWithValue('Failed to get YouTube video insight');
    }
  }
);

// GET TIKTOK POST INSIGHT (tile selection)
export const getTiktokPostInsightAPIFn = createAsyncThunk(
  'managekit/getTiktokPostInsightAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(endPoints.getTiktokPostInsightURL, values?.payload);
      return data;
    } catch (error) {
      if (error?.response?.data) return rejectWithValue(error.response.data);
      return rejectWithValue('Failed to get TikTok post insight');
    }
  }
);

// UPLOAD IMAGES FILE
export const uploadImageFileAPIFn = createAsyncThunk(
  'managekit/uploadImage',
  async ({ file, onProgress }, { rejectWithValue }) => {
    try {
      if (!isAllowedUploadFile(file)) {
        return rejectWithValue(ALLOWED_UPLOAD_ERROR_MESSAGE);
      }

      const cleanName = file.name;
      const cleanFile = new File([file], cleanName, { type: file.type });
      const formData = new FormData();
      formData.append('file', cleanFile);

      const response = await axios({
        method: 'post',
        url: endPoints.uploadImage,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Access-Control-Allow-Origin': '*'
        },
        data: formData,
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent?.total || 1)
          );
          if (onProgress) {
            onProgress(percentCompleted); // Execute the callback if provided
          }
        }
      });

      const imageUrl = response.data?.result?.imageUrl;
      return imageUrl?.includes('https://localhost')
        ? `${coreAPI}/uploads/ji5lDwuiz4.png`
        : imageUrl;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// UPLOAD VIDEO FILE
export const uploadVideoFileAPIFn = createAsyncThunk(
  'managekit/uploadVideoFile',
  async ({ file, onProgress }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios({
        method: 'post',
        url: endPoints.uploadVideo,
        data: formData,
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent?.total || 1)
          );
          if (onProgress) {
            onProgress(percentCompleted);
          }
        }
      });

      return response.data?.result;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// SAVE REVIEWS
export const saveOrUpdateReviewsAPIFn = createAsyncThunk(
  'managekit/saveOrUpdateReviewsAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${endPoints.saveOrUpdateCreateReviews}?callingUserId=${values?.creatorId}`,
        values
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// SEND MAIL TO BRANCH
export const sendMailToBrandAPIFn = createAsyncThunk(
  'managekit/sendMailToBrandAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${endPoints.sendMailToBrandURL}?url=${values?.link}`, values?.mailData);
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// MANAGE MEDIA KIT SLICE
export const manageMediaKitSlice = createSlice({
  name: 'manageMediaKit',
  initialState,
  reducers: {
    setSocialPostsCacheEntry: (state, action) => {
      const { key, posts, fetchedAt, nextCursor, hasMore } = action.payload || {};
      if (!key) return;
      state.socialPostsCache[key] = {
        posts: Array.isArray(posts) ? posts : [],
        fetchedAt: fetchedAt || Date.now(),
        nextCursor: nextCursor || null,
        hasMore: Boolean(hasMore)
      };
    },
    clearSocialPostsCache: (state) => {
      state.socialPostsCache = {};
    }
  },
  extraReducers: builder => {
    builder // SHAREABLE MEDIA KIT DATA
      .addCase(getLiveMediaKitURLAPIFn.pending, state => {
        state.loading = true;
        state.error = null;
        state.shareableData = null;
      })
      .addCase(getLiveMediaKitURLAPIFn.fulfilled, (state, action) => {
        state.loading = false;
        state.shareableData = {
          link: action.payload?.result
        };
        state.error = null;
      }) // AUTHENTICATE MEDIA KIT SOCIAL DATA
      .addCase(getLiveMediaKitURLAPIFn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch shareable link';
        state.shareableData = null;
      })
      .addCase(getLiveMediaKitSocialURLAPIFn.pending, state => {
        state.loading = true;
        state.error = null;
        state.socialMediaKitData = null;
      })
      .addCase(getLiveMediaKitSocialURLAPIFn.fulfilled, (state, action) => {
        state.loading = false;
        state.socialMediaKitData = action.payload;
        state.error = null;
      })
      .addCase(getLiveMediaKitSocialURLAPIFn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.socialMediaKitData = null;
      })
      // REMOVE SOCIAL CHANNEL AUTHENTICATION
      .addCase(removeAuthenticateSocialAPIFn.pending, (state) => {
        state.removeAuthLoading = true;
        state.error = null;
      })
      .addCase(removeAuthenticateSocialAPIFn.fulfilled, (state) => {
        state.removeAuthLoading = false;
        state.error = null;
      })
      .addCase(removeAuthenticateSocialAPIFn.rejected, (state, action) => {
        state.removeAuthLoading = false;
        state.error = action.payload || 'Failed to remove social authentication';
      })
      // DELETE MEDIA KIT
      .addCase(deleteMediaKitAPIFn.pending, (state) => {
        state.deleteMediaKitLoading = true;
        state.error = null;
      })
      .addCase(deleteMediaKitAPIFn.fulfilled, (state) => {
        state.deleteMediaKitLoading = false;
        state.error = null;
      })
      .addCase(deleteMediaKitAPIFn.rejected, (state, action) => {
        state.deleteMediaKitLoading = false;
        state.error = action.payload || 'Failed to delete media kit';
      })
      // VERIFY SOCIAL MEDIA EXISTENCE
      .addCase(verifySocialMediaExistenceAPIFn.pending, (state) => {
        state.verifySocialLoading = true;
        state.error = null;
      })
      .addCase(verifySocialMediaExistenceAPIFn.fulfilled, (state) => {
        state.verifySocialLoading = false;
        state.error = null;
      })
      .addCase(verifySocialMediaExistenceAPIFn.rejected, (state, action) => {
        state.verifySocialLoading = false;
        state.error = action.payload || 'Failed to verify social media existence';
      })
      // GET SOCIAL POST FROM URL
      .addCase(getSocialPostFromUrlAPIFn.pending, (state) => {
        state.getSocialPostLoading = true;
        state.error = null;
      })
      .addCase(getSocialPostFromUrlAPIFn.fulfilled, (state) => {
        state.getSocialPostLoading = false;
        state.error = null;
      })
      .addCase(getSocialPostFromUrlAPIFn.rejected, (state, action) => {
        state.getSocialPostLoading = false;
        state.error = action.payload || 'Failed to get social post';
      })
      // GET SOCIAL POSTS LIST
      .addCase(getSocialMediaPostsListAPIFn.pending, (state) => {
        state.getSocialPostsListLoading = true;
        state.error = null;
      })
      .addCase(getSocialMediaPostsListAPIFn.fulfilled, (state) => {
        state.getSocialPostsListLoading = false;
        state.error = null;
      })
      .addCase(getSocialMediaPostsListAPIFn.rejected, (state, action) => {
        state.getSocialPostsListLoading = false;
        state.error = action.payload || 'Failed to get social media posts';
      })
      // GET INSTAGRAM POST INSIGHT
      .addCase(getInstagramPostInsightAPIFn.pending, (state) => {
        state.getInstagramPostInsightLoading = true;
        state.error = null;
      })
      .addCase(getInstagramPostInsightAPIFn.fulfilled, (state) => {
        state.getInstagramPostInsightLoading = false;
        state.error = null;
      })
      .addCase(getInstagramPostInsightAPIFn.rejected, (state, action) => {
        state.getInstagramPostInsightLoading = false;
        state.error = action.payload || 'Failed to get Instagram post insight';
      })
      // GET YOUTUBE VIDEO INSIGHT
      .addCase(getYoutubeVideoInsightAPIFn.pending, (state) => {
        state.getYoutubeVideoInsightLoading = true;
        state.error = null;
      })
      .addCase(getYoutubeVideoInsightAPIFn.fulfilled, (state) => {
        state.getYoutubeVideoInsightLoading = false;
        state.error = null;
      })
      .addCase(getYoutubeVideoInsightAPIFn.rejected, (state, action) => {
        state.getYoutubeVideoInsightLoading = false;
        state.error = action.payload || 'Failed to get YouTube video insight';
      })
      // GET TIKTOK POST INSIGHT
      .addCase(getTiktokPostInsightAPIFn.pending, (state) => {
        state.getTiktokPostInsightLoading = true;
        state.error = null;
      })
      .addCase(getTiktokPostInsightAPIFn.fulfilled, (state) => {
        state.getTiktokPostInsightLoading = false;
        state.error = null;
      })
      .addCase(getTiktokPostInsightAPIFn.rejected, (state, action) => {
        state.getTiktokPostInsightLoading = false;
        state.error = action.payload || 'Failed to get TikTok post insight';
      })
      // UPLOAD IMAGE FILE
      .addCase(uploadImageFileAPIFn.pending, (state) => {
        state.uploadImageLoading = true;
        state.error = null;
      })
      .addCase(uploadImageFileAPIFn.fulfilled, (state) => {
        state.uploadImageLoading = false;
        state.error = null;
      })
      .addCase(uploadImageFileAPIFn.rejected, (state, action) => {
        state.uploadImageLoading = false;
        state.error = action.payload || 'Failed to upload image';
      })
      // UPLOAD VIDEO FILE
      .addCase(uploadVideoFileAPIFn.pending, (state) => {
        state.uploadVideoLoading = true;
        state.error = null;
      })
      .addCase(uploadVideoFileAPIFn.fulfilled, (state) => {
        state.uploadVideoLoading = false;
        state.error = null;
      })
      .addCase(uploadVideoFileAPIFn.rejected, (state, action) => {
        state.uploadVideoLoading = false;
        state.error = action.payload || 'Failed to upload video';
      })
      // SAVE OR UPDATE REVIEWS
      .addCase(saveOrUpdateReviewsAPIFn.pending, (state) => {
        state.saveReviewsLoading = true;
        state.error = null;
      })
      .addCase(saveOrUpdateReviewsAPIFn.fulfilled, (state) => {
        state.saveReviewsLoading = false;
        state.error = null;
      })
      .addCase(saveOrUpdateReviewsAPIFn.rejected, (state, action) => {
        state.saveReviewsLoading = false;
        state.error = action.payload || 'Failed to save reviews';
      })
      // SEND MAIL TO BRAND
      .addCase(sendMailToBrandAPIFn.pending, (state) => {
        state.sendMailLoading = true;
        state.error = null;
      })
      .addCase(sendMailToBrandAPIFn.fulfilled, (state) => {
        state.sendMailLoading = false;
        state.error = null;
      })
      .addCase(sendMailToBrandAPIFn.rejected, (state, action) => {
        state.sendMailLoading = false;
        state.error = action.payload || 'Failed to send mail';
      });
  }
});

export const {
  setSocialPostsCacheEntry,
  clearSocialPostsCache
} = manageMediaKitSlice.actions;

export default manageMediaKitSlice.reducer;
